// RecordingsSection.tsx — onglet « Enregistrements » du Dashboard : les dernières
// prises faites depuis la régie (Rec)
//
// Refait le 2026-10-17 d'après la maquette validée par l'utilisateur
// (https://claude.ai/artifact/MtJ8i9W8i9G2uHH3CcnL7N), sur le modèle de l'onglet
// Chaînes. En plus : la prise en cours en grand avec son compteur, et une frise
// heure par heure (une ligne par chaîne, un bloc par prise) pour voir quand les
// chaînes enregistrent. Une prise Multi view n'occupe qu'une carte : admin-api range
// ses angles dans `angles`. Données : admin-api DashboardRecordings (`recordings` et
// `recordingStats`).

import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';
import { WWW_URLS } from '../../../backendServices/urls';
import { Geo } from './ActivityOverview';
import { ListTable, TitleCell, CreatorCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Owner, Segment, usePicked, ViewToggle, EmptyState, Spark, useView, groupByDay, dayLabel, hour, clock, spoken, size,
         sizeParts, plural, tintOf, openChannel, shortDay, creatorPlace, CREATOR_PLACE_HINT } from './dashboardShared';

export interface DashboardRecording {
  id              : number;
  name            : string;
  format          : string;
  channelId       : number;
  channelName     : string;
  publicRef       : string;
  channelOn       : boolean;
  owner           : string;
  ownerEmail      : string;
  durationSeconds : number | null;
  sizeBytes       : number | null;
  fileUrl         : string | null;
  creationDate    : string;
  stoppedAt       : string | null;
  recording       : boolean;
  elapsedSeconds  : number | null;
  angles          : { id: number; name: string; sizeBytes: number | null; fileUrl: string | null }[];
  geo?            : Geo | null;
}

export interface RecordingStats {
  live         : { channelName: string; elapsedSeconds: number }[];
  todayTakes   : number;
  todaySeconds : number;
  weekSeconds  : number;
  days         : { day: string; seconds: number; takes: number }[];
  totalBytes   : number;
  todayBytes   : number;
}

export type RecordingKpi = 'live' | 'today' | 'week' | 'space' | null;

interface Props {
  recordings : DashboardRecording[];
  stats      : RecordingStats | null;
  loading    : boolean;
  error      : boolean;
  filtered   : boolean;
  onClearFilters : () => void;
  onPlayFile : (r: DashboardRecording) => void;
  onWatchLive: (r: DashboardRecording) => void;
  /** Graphe, carte et villes de la période (ActivityOverview), placés sous les chiffres. */
  overview?: React.ReactNode;
  /** Éléments du point désigné sur la carte : surlignés, le premier amené à l'écran. */
  picked?: DashboardRecording[];
  kpi?   : RecordingKpi;
  onKpi? : (kpi: RecordingKpi) => void;
}

export type RecState = 'live' | 'done' | 'nofile';

const VIEW_KEY = 'dashboardV2.recordingsView';
export const stateOf = (r: DashboardRecording): RecState => r.recording ? 'live' : (!r.sizeBytes ? 'nofile' : 'done');
export const contentText = (r: DashboardRecording) => r.angles.length > 0 ? `Multi view · ${plural(r.angles.length + 1, 'fichier')}` : 'Sortie programme';
export const totalBytes  = (r: DashboardRecording) => (r.sizeBytes ?? 0) + r.angles.reduce((s, a) => s + (a.sizeBytes ?? 0), 0);

/** Heure locale en fraction de jour (14 h 30 → 14,5) dans le jour qui commence à `dayStart`. */
const hoursIn = (iso: string, dayStart: number) => (new Date(iso).getTime() - dayStart) / 3_600_000;

// ── Petits morceaux ───────────────────────────────────────────────────────────

export const StateBadge: React.FC<{ r: DashboardRecording }> = ({ r }) => {
  switch (stateOf(r)) {
    case 'live'  : return <span className={`${styles.badge} ${tabs.badgeRed}`}><span className={tabs.dotPulse}/>REC</span>;
    case 'nofile': return <span className={`${styles.badge} ${tabs.badgeAmber}`}>SANS FICHIER</span>;
    default      : return <span className={`${styles.badge} ${tabs.badgeBlue}`}>TERMINÉE</span>;
  }
};

export const Thumb: React.FC<{ r: DashboardRecording; elapsed: number | null; big?: boolean }> = ({ r, elapsed, big }) => {
  const [ failed, setFailed ] = useState(false);
  const src = r.publicRef ? `${WWW_URLS.MediaServer}${r.publicRef}/images/pending.webp` : '';
  return (
    <div className={`${styles.thumb} ${stateOf(r) === 'nofile' ? tabs.veil : ''}`} style={{ background: tintOf(r.channelId) }}>
      {src && !failed
        ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
        : <span className={big ? styles.thumbNameBig : styles.thumbName}>{r.channelName}</span>}
      {/* position forcée : `td .badges` la rend statique quand les cartes sont dans une cellule (fiche créateur, Ressources) */}
      <span className={styles.badges} style={{ zIndex: 1, position: 'absolute' }}><StateBadge r={r} /></span>
      <span className={tabs.corner}>{r.recording ? `${spoken(elapsed)} ●` : (spoken(r.durationSeconds) || '—')}</span>
    </div>
  );
};

/** Vignette de la vue liste : l'image de la chaîne, un point rouge pendant la prise. */
export const MiniThumb: React.FC<{ r: DashboardRecording }> = ({ r }) => {
  const [ failed, setFailed ] = useState(false);
  const src = r.publicRef ? `${WWW_URLS.MediaServer}${r.publicRef}/images/pending.webp` : '';
  return (
    <div className={stateOf(r) === 'nofile' ? tabs.veil : undefined} style={{ width: '100%', height: '100%', background: tintOf(r.channelId) }}>
      {src && !failed && <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />}
      {r.recording && <span className={tabs.dotPulse} style={{ position: 'absolute', top: 4, right: 4 }} aria-label="Enregistrement en cours" />}
    </div>
  );
};

/**
 * Lien de téléchargement forcé : le fichier est servi par le serveur de médias, une
 * autre origine — le navigateur y ignore l'attribut `download` et ouvrait la vidéo dans
 * un onglet. `dl=1` lui fait renvoyer Content-Disposition: attachment (même convention
 * que resolveFileUrl du panneau Stockage de la régie).
 */
/** Ancre d'une prise dans la liste (carte ou ligne) : cible du clic dans la frise. */
const recAnchorId = (id: number) => `dash-rec-${id}`;
const SELECTED_ROW : React.CSSProperties = { background: 'rgba(44,125,225,0.22)', boxShadow: 'inset 4px 0 0 #2C7DE1' };
const SELECTED_CARD: React.CSSProperties = { outline: '2px solid #5D97F1', outlineOffset: 2 };

const downloadUrl = (fileUrl: string) => `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}dl=1`;

const DL_LINK: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' };
const DL_MENU: React.CSSProperties = {
  position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 20, minWidth: 200,
  display: 'flex', flexDirection: 'column', padding: 4, borderRadius: 10,
  background: '#0f172a', border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 12px 28px rgba(0,0,0,0.5)',
};
const DL_MENU_ITEM: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7,
  fontSize: 13, color: '#e2e8f0', textDecoration: 'none', whiteSpace: 'nowrap',
};

// `noRegie` : sans le bouton Régie — la vue liste n'en veut pas (2026-10-24).
export const Actions: React.FC<{ r: DashboardRecording; onPlayFile: Props['onPlayFile']; onWatchLive: Props['onWatchLive']; big?: boolean; noRegie?: boolean }> =
  ({ r, onPlayFile, onWatchLive, big, noRegie }) => (
  <div className={big ? styles.actionsBig : styles.actions}>
    {r.recording
      ? <button type="button" className={styles.primary} disabled={!r.channelOn || !r.publicRef} onClick={() => onWatchLive(r)}>{big ? 'Voir le direct' : 'Direct'}</button>
      : <button type="button" className={styles.primary} disabled={!r.fileUrl}
                title={!r.fileUrl ? 'Fichier pas encore rangé dans l’espace du créateur' : undefined}
                onClick={() => onPlayFile(r)}>Lire</button>}
    {!r.recording && r.fileUrl &&
      <a className={styles.secondary} href={downloadUrl(r.fileUrl)} download={`${r.name}.${r.format}`} rel="noreferrer"
         title={`Télécharger ${r.name}.${r.format}`} style={DL_LINK}>
        <Download size={14} aria-hidden="true" />{big || r.angles.length === 0 ? 'Télécharger' : 'Programme'}
      </a>}
    {/* Multi view : chaque angle est un fichier à part (2026-10-23). */}
    {!r.recording && r.angles.some(a => a.fileUrl) && (
      <details style={{ position: 'relative' }}>
        <summary className={styles.secondary} style={{ ...DL_LINK, listStyle: 'none', cursor: 'pointer' }} title="Télécharger un angle">
          <Download size={14} aria-hidden="true" />Angles ({r.angles.filter(a => a.fileUrl).length})
        </summary>
        <div style={DL_MENU}>
          {r.angles.filter(a => a.fileUrl).map(a => (
            <a key={a.id} href={downloadUrl(a.fileUrl!)} download rel="noreferrer" style={DL_MENU_ITEM}>
              <Download size={13} aria-hidden="true" /> {a.name}{a.sizeBytes ? ` · ${size(a.sizeBytes)}` : ''}
            </a>
          ))}
        </div>
      </details>
    )}
    {!noRegie && <button type="button" className={styles.secondary} onClick={() => openChannel(r.channelId, 'regie')}>{big ? 'Ouvrir la régie' : 'Régie'}</button>}
  </div>
);

// ── Frise d'une journée ──────────────────────────────────────────────────────

const Timeline: React.FC<{ recordings: DashboardRecording[]; now: number; onPick: (r: DashboardRecording) => void }> = ({ recordings, now, onPick }) => {
  const [ which, setWhich ] = useState<'today' | 'yesterday'>('today');
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const dayStart = which === 'today' ? today.getTime() : today.getTime() - 86_400_000;
  const dayEnd   = dayStart + 86_400_000;

  const rows = useMemo(() => {
    const out: { channelId: number; name: string; takes: DashboardRecording[] }[] = [];
    for (const r of recordings) {
      const start = new Date(r.creationDate).getTime();
      const stop  = r.stoppedAt ? new Date(r.stoppedAt).getTime() : now;
      if (stop < dayStart || start >= dayEnd) continue;
      const row = out.find(x => x.channelId === r.channelId) ?? (out.push({ channelId: r.channelId, name: r.channelName, takes: [] }), out[out.length - 1]);
      row.takes.push(r);
    }
    return out;
  }, [recordings, dayStart, dayEnd, now]);

  const pct = (h: number) => `${Math.max(0, Math.min(24, h)) / 24 * 100}%`;
  const nowH = hoursIn(new Date(now).toISOString(), dayStart);

  return (
    <div className={tabs.timeline}>
      <div className={tabs.tlHead}>
        <h3>{which === 'today' ? "Aujourd'hui" : 'Hier'}, heure par heure</h3>
        <span className={styles.muted} style={{ fontSize: '0.85rem' }}>une ligne par chaîne, un bloc par prise</span>
        <span className={styles.spacer}/>
        <Segment label="Jour" value={which} onChange={setWhich} choices={[ ['today', "Aujourd'hui"], ['yesterday', 'Hier'] ] as const} />
      </div>
      {rows.length === 0
        ? <div className={styles.empty}>Aucune prise {which === 'today' ? "aujourd'hui" : 'hier'} parmi les enregistrements affichés.</div>
        : <div className={tabs.tlScroll}>
            <div className={tabs.tlGrid}>
              {rows.map(row => (
                <React.Fragment key={row.channelId}>
                  <span className={tabs.tlLabel} title={row.name}>{row.name}</span>
                  <div className={tabs.tlTrack}>
                    {row.takes.map(r => {
                      const a = hoursIn(r.creationDate, dayStart);
                      const b = r.stoppedAt ? hoursIn(r.stoppedAt, dayStart) : nowH;
                      const cls = r.recording ? `${tabs.tlBlock} ${tabs.tlLive}` : stateOf(r) === 'nofile' ? `${tabs.tlBlock} ${tabs.tlEmptyFile}` : tabs.tlBlock;
                      return <button key={r.id} type="button" className={cls}
                                     style={{ left: pct(a), width: `calc(${pct(b)} - ${pct(a)})` }}
                                     title={`${r.name} · ${hour(r.creationDate)} → ${r.stoppedAt ? hour(r.stoppedAt) : 'en cours'}`}
                                     aria-label={`${r.channelName}, prise de ${hour(r.creationDate)}`}
                                     onClick={() => onPick(r)} />;
                    })}
                    {which === 'today' && <span className={tabs.tlNow} style={{ left: pct(nowH) }}/>}
                  </div>
                </React.Fragment>
              ))}
              <span/>
              <div className={tabs.tlAxis}>
                {[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => <span key={h} style={{ left: pct(h) }}>{String(h).padStart(2, '0')} h</span>)}
              </div>
            </div>
          </div>}
    </div>
  );
};

// ── Composant ────────────────────────────────────────────────────────────────

const RecordingsSection: React.FC<Props> = ({ recordings, stats, loading, error, filtered, onClearFilters, onPlayFile, onWatchLive, overview, picked, kpi, onKpi }) => {
  const { ref: pickRef, mark } = usePicked(picked, (r: DashboardRecording) => r.id);
  const [ state, setState ] = useState<'all' | RecState>('all');
  const [ view, setView ] = useView(VIEW_KEY);

  // Le compteur des prises en cours avance chaque seconde à partir de l'écoulé que
  // le serveur a compté (sur son horloge UTC) au moment du relevé.
  const [ now, setNow ] = useState(() => Date.now());
  const loadedAt = useMemo(() => Date.now(), [recordings]);   // eslint-disable-line react-hooks/exhaustive-deps
  const anyLive  = recordings.some(r => r.recording);
  useEffect(() => {
    if (!anyLive) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [anyLive]);
  const elapsedOf = (r: DashboardRecording) => r.recording && r.elapsedSeconds != null ? r.elapsedSeconds + Math.max(0, (now - loadedAt) / 1000) : null;

  const shown  = useMemo(() => recordings.filter(r => state === 'all' || stateOf(r) === state), [recordings, state]);
  // Vue en cartes : toutes de la même taille (la prise en cours ou la plus récente n'est plus mise en grand).
  const groups = useMemo(() => groupByDay(shown, r => r.creationDate), [shown]);
  // Clic sur un bloc de la frise (2026-10-23) : sélectionne la prise dans la liste du
  // dessous — défilement jusqu'à elle, surlignage, bref éclat — au lieu de la lancer.
  // Lire / Direct / Régie restent sur ses boutons. Un filtre d'état qui la masquerait
  // est levé. `selectTick` relance le défilement même sur la prise déjà choisie.
  const [ selectedId, setSelectedId ] = useState<number | null>(null);
  const [ selectTick, setSelectTick ] = useState(0);
  const pick = (r: DashboardRecording) => {
    if (state !== 'all' && stateOf(r) !== state) setState('all');
    setSelectedId(r.id);
    setSelectTick(t => t + 1);
  };
  useEffect(() => {
    if (selectedId === null || selectTick === 0) return;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(recAnchorId(selectedId));
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.animate?.([ { boxShadow: '0 0 0 3px #5D97F1, 0 0 24px rgba(93,151,241,0.6)' }, { boxShadow: '0 0 0 0 rgba(93,151,241,0)' } ],
                   { duration: 1800, delay: 350, easing: 'ease-out' });
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedId, selectTick, view]);
  /** Attributs d'une carte ou d'une ligne : ancre, désignation sur la carte, sélection depuis la frise. */
  const rowAttrs = (r: DashboardRecording, row = false) => {
    const m = mark(r, row) as { style?: React.CSSProperties };
    const sel = r.id === selectedId;
    return { ...m, id: recAnchorId(r.id), 'aria-selected': sel || undefined,
             style: sel ? { ...m.style, ...(row ? SELECTED_ROW : SELECTED_CARD) } : m.style };
  };

  const live = stats?.live ?? [];
  const [ bytesValue, bytesUnit ] = sizeParts(stats?.totalBytes ?? 0);

  return (
    <div className={styles.root} ref={pickRef}>

      {/* ── Chiffres ── */}
      <div className={styles.kpis}>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${live.length > 0 ? tabs.kpiAlert : ''} ${kpi === 'live' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'live'}
                title="Cliquer pour ne montrer que les enregistrements en cours (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'live' ? null : 'live')}>
          <span className={styles.kpiLabel}>Rec en cours</span>
          <span className={`${styles.kpiValue} ${live.length > 0 ? tabs.red : ''}`}>{stats ? live.length : '–'}</span>
          <span className={styles.kpiMuted}>
            {live.length === 0 ? 'aucune chaîne n’enregistre' : live.slice(0, 2).map(l => `${l.channelName} depuis ${spoken(l.elapsedSeconds)}`).join(', ') + (live.length > 2 ? '…' : '')}
          </span>
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'today' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'today'}
                title="Cliquer pour ne montrer que les prises du jour (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'today' ? null : 'today')}>
          <span className={styles.kpiLabel}>Aujourd'hui</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow}`}>{stats?.todayTakes ?? '–'}{stats && stats.todaySeconds > 0 && <small>· {spoken(stats.todaySeconds)}</small>}</span>
          {stats && <span className={styles.kpiMuted}>{plural(stats.todayTakes, 'prise')}</span>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'week' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'week'}
                title="Cliquer pour ne montrer que les prises sur 7 jours (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'week' ? null : 'week')}>
          <span className={styles.kpiLabel}>Heures enregistrées, 7 jours</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow}`}>{stats ? <>{Math.round(stats.weekSeconds / 3600).toLocaleString('fr-FR')}<small>h</small></> : '–'}</span>
          {stats && <Spark values={stats.days.map(d => ({ key: d.day, value: d.seconds, title: `${shortDay(d.day)} : ${spoken(d.seconds) || '0 min'}` }))} />}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'space' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'space'}
                title="Cliquer pour montrer tous les enregistrements, toutes dates (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'space' ? null : 'space')}>
          <span className={styles.kpiLabel}>Espace des enregistrements</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow}`}>{stats ? <>{bytesValue}<small>{bytesUnit}</small></> : '–'}</span>
          {stats && <span className={styles.kpiMuted}>{stats.todayBytes > 0 ? `+ ${size(stats.todayBytes)} aujourd'hui` : 'rien de nouveau aujourd’hui'}</span>}
        </button>
      </div>

      {overview}

      {/* ── Frise des prises ── */}
      {!error && recordings.length > 0 && <Timeline recordings={recordings} now={now} onPick={pick} />}

      {/* ── Filtres de l'onglet ── */}
      <div className={styles.toolbar}>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', 'Toutes les prises'], ['live', 'En cours'], ['done', 'Terminées'], ['nofile', 'Sans fichier'] ] as const} />
        <span className={styles.spacer}/>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {error && <div className={styles.empty}>Chargement impossible.</div>}

      {!error && shown.length === 0 &&
        <EmptyState loading={loading} title="Aucun enregistrement sur cette période"
                    hint="Élargissez la période ou retirez un filtre. Une prise lancée par Rec apparaît ici dès son début."
                    canClear={filtered || state !== 'all'}
                    onClear={() => { setState('all'); onClearFilters(); }} />}

      {/* ── Vue cartes ── */}
      {!error && view === 'cards' && groups.map(g => (
        <section key={g.title} className={styles.day}>
          <h3 className={styles.dayTitle}>{g.title} <span className={styles.muted}>{plural(g.items.length, 'prise')}</span></h3>
          <div className={styles.cards}>
            {g.items.map(r => (
              <article key={r.id} {...rowAttrs(r)} className={`${styles.card} ${r.recording ? tabs.cardRec : stateOf(r) === 'nofile' ? tabs.cardWarn : ''}`}>
                <Thumb r={r} elapsed={elapsedOf(r)} />
                <div className={styles.cardBody}>
                  <span className={`${styles.cardName} ${tabs.mono}`} title={`${r.name}.${r.format}`}>{r.name}.{r.format}</span>
                  <Owner owner={r.owner} email={r.ownerEmail} small />
                  <div className={tabs.cardMeta}>
                    <span>{hour(r.creationDate)} → {r.recording ? <span className={tabs.red}>en cours</span> : (hour(r.stoppedAt) || '?')}</span>
                    {size(totalBytes(r)) && <span>{size(totalBytes(r))}</span>}
                  </div>
                  <div className={styles.chips}>
                    <span>{r.channelName}</span>
                    <span>{contentText(r)}</span>
                    {creatorPlace(r.geo) && <span title={CREATOR_PLACE_HINT}>📍 {creatorPlace(r.geo)}</span>}
                    {stateOf(r) === 'nofile' && <span className={tabs.chipWarn}>Aucun fichier produit</span>}
                  </div>
                  <Actions r={r} onPlayFile={onPlayFile} onWatchLive={onWatchLive} />
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}

      {/* ── Vue liste ── */}
      {!error && view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={r => r.id} rowProps={r => rowAttrs(r, true)} dateOf={r => r.creationDate}
                   noun={{ one: 'prise', many: 'prises' }} columns={[
          { id: 'take', label: 'Prise', cell: r =>
              <TitleCell thumb={<MiniThumb r={r} />} title={r.channelName} titleHint={`${r.name}.${r.format}`}
                         sub={<><span className={tabs.mono}>{r.name}.{r.format}</span> · {contentText(r)}</>} /> },
          { id: 'state', label: 'État', cell: r => <StateBadge r={r} /> },
          { id: 'creator', label: 'Créateur', cell: r => <CreatorCell owner={r.owner} email={r.ownerEmail} geo={r.geo} /> },
          { id: 'duration', label: 'Durée', right: true, sort: (x, y) => (y.durationSeconds ?? -1) - (x.durationSeconds ?? -1),
            cell: r => r.recording ? clock(elapsedOf(r)) : (clock(r.durationSeconds) || <Dash/>) },
          { id: 'size', label: 'Taille', right: true, sort: (x, y) => totalBytes(y) - totalBytes(x), cell: r => size(totalBytes(r)) || <Dash/> },
          { id: 'date', label: 'Début', cell: r => <WhenCell iso={r.creationDate} /> },
          { id: 'actions', label: '', right: true, cell: r => <RowActions><Actions r={r} onPlayFile={onPlayFile} onWatchLive={onWatchLive} noRegie /></RowActions> },
        ]} />
      )}
    </div>
  );
};

export default RecordingsSection;
